import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Home, FileText, CreditCard, LineChart, UserCircle2,
  ChevronLeft, Ticket, Bell, Clock, ScanLine, Plus, Search,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ScanVoucherModal from '../components/ScanVoucherModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const PRIMARY = '#2D5BFF';
const PRIMARY_DARK = '#1B3DBF';
const GRADIENT_HEADER = `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`;

const NAV_ITEMS = [
  { icon: Home, label: 'Home', key: 'home' },
  { icon: FileText, label: 'Receipts', key: 'receipts' },
  { icon: CreditCard, label: 'Cards', key: 'cards' },
  { icon: LineChart, label: 'Insights', key: 'insights' },
  { icon: UserCircle2, label: 'Profile', key: 'profile' },
];

// ── Design tokens ──────────────────────────────────────
const STATUS_STYLES = {
  expiring_soon: { bg: '#FFF7ED', text: '#EA580C', dot: '#F59E0B', label: 'Expiring soon' },
  active:        { bg: '#F0FDF4', text: '#16A34A', dot: '#22C55E', label: 'Active' },
  used:          { bg: '#F3F4F6', text: '#6B7280', dot: '#9CA3AF', label: 'Used' },
  expired:       { bg: '#FEF2F2', text: '#DC2626', dot: '#EF4444', label: 'Expired' },
};

const TYPE_COLORS = {
  money_off:      { bg: '#E8F5E9', text: '#2E7D32' },
  product_deal:   { bg: '#E0F2F1', text: '#00796B' },
  free_product:   { bg: '#E8EAF6', text: '#3949AB' },
  reward_voucher: { bg: '#FFF3E0', text: '#E65100' },
  points_booster: { bg: '#F3E5F5', text: '#7B1FA2' },
  percentage_off: { bg: '#FCE4EC', text: '#C62828' },
};

const TYPE_LABELS = {
  money_off:      'MONEY OFF',
  product_deal:   'PRODUCT DEAL',
  free_product:   'FREE PRODUCT',
  reward_voucher: 'REWARD VOUCHER',
  points_booster: 'POINTS BOOSTER',
  percentage_off: '% OFF',
};

const RETAILER_COLORS = {
  'Tesco':        { bg: '#1C3FAA', text: '#FFF' },
  "Sainsbury's":  { bg: '#F47721', text: '#FFF' },
  'B&Q':          { bg: '#1A1A1A', text: '#FFF' },
  'M&S':          { bg: '#1A1A1A', text: '#FFF' },
  'Boots':        { bg: '#005B3A', text: '#FFF' },
  'Nectar':       { bg: '#6B21A8', text: '#FFF' },
  'Asda':         { bg: '#78BE20', text: '#FFF' },
  'Waitrose':     { bg: '#5D8C3E', text: '#FFF' },
  'Morrisons':    { bg: '#006F42', text: '#FFF' },
  'Lidl':         { bg: '#0050AA', text: '#FFD700' },
  'Aldi':         { bg: '#00205B', text: '#FFF' },
  'Co-op':        { bg: '#00B1EB', text: '#FFF' },
};

const getRetailerColor = (name) => {
  if (!name) return { bg: '#6B7280', text: '#FFF' };
  if (RETAILER_COLORS[name]) return RETAILER_COLORS[name];
  const lower = name.toLowerCase();
  for (const [key, val] of Object.entries(RETAILER_COLORS)) {
    if (lower.includes(key.toLowerCase())) return val;
  }
  const hue = ((name.charCodeAt(0) || 65) * 137) % 360;
  return { bg: `hsl(${hue}, 55%, 45%)`, text: '#FFF' };
};

const getInitials = (name) => {
  if (!name) return '?';
  const w = name.trim().split(/\s+/);
  return w.length >= 2 ? (w[0][0] + w[1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
};

// ── Helpers ────────────────────────────────────────────
const getVoucherDisplayStatus = (voucher) => {
  if (voucher.status === 'used') return 'used';
  if (voucher.status === 'expired') return 'expired';
  if (voucher.expiry_date) {
    const now = new Date();
    const exp = new Date(voucher.expiry_date + 'T23:59:59');
    const diffDays = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
    if (diffDays <= 7 && diffDays >= 0) return 'expiring_soon';
  }
  return 'active';
};

const getExpiryText = (voucher) => {
  if (voucher.status === 'used' && voucher.used_date) {
    const d = new Date(voucher.used_date);
    return `Used ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }
  if (voucher.status === 'expired' && voucher.expiry_date) {
    const d = new Date(voucher.expiry_date);
    return `Expired ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }
  if (!voucher.expiry_date) return null;
  const now = new Date();
  const exp = new Date(voucher.expiry_date + 'T23:59:59');
  const diffDays = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'Expired';
  if (diffDays === 1) return 'Expires tomorrow';
  if (diffDays <= 7) return `Expires in ${diffDays} days`;
  return `Valid until ${exp.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
};

const getValueDisplay = (voucher) => {
  if (voucher.value_label) return voucher.value_label;
  if (voucher.value != null) {
    const sym = voucher.currency === 'USD' ? '$' : voucher.currency === 'EUR' ? '€' : '£';
    return `${sym}${voucher.value % 1 === 0 ? voucher.value : voucher.value.toFixed(2)}`;
  }
  return '—';
};

// ── User avatar ────────────────────────────────────────
const UserAvatar = ({ name, avatarUrl, size = 40 }) => {
  if (avatarUrl) {
    return (
      <img src={avatarUrl} alt="" style={{ width: size, height: size, borderRadius: 999, objectFit: 'cover' }} />
    );
  }
  const initial = name ? name.charAt(0).toUpperCase() : '?';
  return (
    <div
      className="flex items-center justify-center flex-shrink-0"
      style={{ width: size, height: size, borderRadius: 999, backgroundColor: PRIMARY, color: '#FFF', fontSize: size * 0.38, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}
    >
      {initial}
    </div>
  );
};

// ── Voucher card item ──────────────────────────────────
const VoucherCard = ({ voucher, onClick }) => {
  const displayStatus = getVoucherDisplayStatus(voucher);
  const statusStyle = STATUS_STYLES[displayStatus];
  const typeColor = TYPE_COLORS[voucher.voucher_type] || TYPE_COLORS.money_off;
  const rc = getRetailerColor(voucher.retailer);
  const expiryText = getExpiryText(voucher);
  const valueDisplay = getValueDisplay(voucher);
  const typeLabel = TYPE_LABELS[voucher.voucher_type] || 'VOUCHER';
  const isInactive = displayStatus === 'used' || displayStatus === 'expired';

  return (
    <button
      onClick={() => onClick(voucher)}
      className="w-full text-left flex items-center gap-4"
      style={{
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        padding: '16px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        opacity: isInactive ? 0.65 : 1,
        transition: 'transform 0.1s, box-shadow 0.1s',
      }}
      data-testid={`voucher-card-${voucher.id}`}
    >
      {/* Value badge */}
      <div
        className="flex flex-col items-center justify-center flex-shrink-0"
        style={{
          width: 76,
          height: 76,
          borderRadius: 14,
          backgroundColor: isInactive ? '#F3F4F6' : typeColor.bg,
        }}
      >
        <span
          style={{
            color: isInactive ? '#9CA3AF' : typeColor.text,
            fontSize: valueDisplay.length > 5 ? 13 : valueDisplay.length > 3 ? 18 : 22,
            fontWeight: 800,
            fontFamily: 'Manrope, sans-serif',
            lineHeight: 1.1,
            textAlign: 'center',
          }}
        >
          {valueDisplay}
        </span>
        <span
          style={{
            color: isInactive ? '#9CA3AF' : typeColor.text,
            fontSize: 9,
            fontWeight: 700,
            fontFamily: 'DM Sans, sans-serif',
            marginTop: 4,
            textAlign: 'center',
            lineHeight: 1.1,
          }}
        >
          {typeLabel}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p
            className="font-bold line-clamp-2"
            style={{
              color: isInactive ? '#6B7280' : '#111827',
              fontSize: 14,
              fontFamily: 'Manrope, sans-serif',
              lineHeight: 1.3,
            }}
          >
            {voucher.description || voucher.retailer}
          </p>
          {/* Status badge */}
          <span
            className="flex-shrink-0 flex items-center gap-1 rounded-full"
            style={{
              backgroundColor: statusStyle.bg,
              color: statusStyle.text,
              padding: '3px 10px',
              fontSize: 11,
              fontWeight: 600,
              fontFamily: 'DM Sans, sans-serif',
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: 999, backgroundColor: statusStyle.dot, display: 'inline-block' }} />
            {statusStyle.label}
          </span>
        </div>

        {/* Retailer + expiry */}
        <div className="flex items-center gap-2 mt-2">
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{
              width: 24,
              height: 24,
              borderRadius: 7,
              backgroundColor: rc.bg,
              color: rc.text,
              fontSize: 9,
              fontWeight: 700,
              fontFamily: 'Manrope, sans-serif',
            }}
          >
            {getInitials(voucher.retailer)}
          </div>
          <span
            style={{
              color: '#6B7280',
              fontSize: 13,
              fontFamily: 'DM Sans, sans-serif',
            }}
          >
            {voucher.retailer}
          </span>
          {expiryText && (
            <>
              <span style={{ color: '#D1D5DB' }}>·</span>
              <Clock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: displayStatus === 'expiring_soon' ? '#EA580C' : '#9CA3AF' }} />
              <span
                className="truncate"
                style={{
                  color: displayStatus === 'expiring_soon' ? '#EA580C' : '#6B7280',
                  fontSize: 12,
                  fontFamily: 'DM Sans, sans-serif',
                  fontWeight: displayStatus === 'expiring_soon' ? 600 : 400,
                }}
              >
                {expiryText}
              </span>
            </>
          )}
        </div>
      </div>
    </button>
  );
};

// ── Buddy mascot (inline SVG to avoid dependency) ─────
const BuddyMascot = ({ size = 100 }) => (
  <div
    className="flex items-center justify-center"
    style={{
      width: size,
      height: size,
      borderRadius: size * 0.35,
      background: 'linear-gradient(180deg, #4ECDC4 0%, #2D5BFF 100%)',
    }}
  >
    <Ticket className="text-white" style={{ width: size * 0.45, height: size * 0.45 }} strokeWidth={1.5} />
  </div>
);

// ════════════════════════════════════════════════════════
//  MAIN PAGE COMPONENT
// ════════════════════════════════════════════════════════

export default function VouchersPage() {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [vouchers, setVouchers] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeNav, setActiveNav] = useState('home');
  const [showScanModal, setShowScanModal] = useState(false);

  // Responsive check
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Fetch vouchers
  const fetchVouchers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vouchers`, { withCredentials: true });
      setVouchers(res.data.vouchers || []);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/');
        return;
      }
      toast.error('Could not load vouchers');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchVouchers(); }, [fetchVouchers]);

  // Nav handler
  const onNavClick = (key) => {
    setActiveNav(key);
    const routes = { home: '/dashboard', receipts: '/receipts', cards: '/cards', insights: '/insights', profile: '/profile' };
    navigate(routes[key] || '/dashboard');
  };

  // Derived data
  const activeVouchers = vouchers.filter(v => v.status === 'active');
  const usedVouchers = vouchers.filter(v => v.status === 'used');
  const expiredVouchers = vouchers.filter(v => v.status === 'expired');

  const expiringSoon = activeVouchers.filter(v => {
    if (!v.expiry_date) return false;
    const diff = Math.ceil((new Date(v.expiry_date + 'T23:59:59') - new Date()) / (1000 * 60 * 60 * 24));
    return diff >= 0 && diff <= 7;
  });


  // Filtered list
  const filteredVouchers = (() => {
    switch (activeFilter) {
      case 'expiring_soon':
        return expiringSoon;
      case 'active':
        return activeVouchers;
      case 'used':
        return usedVouchers;
      default: {
        // "All" — show active first (expiring first), then used, then expired
        const withPriority = vouchers.map(v => {
          const ds = getVoucherDisplayStatus(v);
          const priority = ds === 'expiring_soon' ? 0 : ds === 'active' ? 1 : ds === 'used' ? 2 : 3;
          return { ...v, _priority: priority };
        });
        withPriority.sort((a, b) => a._priority - b._priority);
        return withPriority;
      }
    }
  })();

  // Apply search on top of active filter
  const displayVouchers = searchQuery.trim()
    ? filteredVouchers.filter(v =>
        (v.retailer || '').toLowerCase().includes(searchQuery.trim().toLowerCase())
      )
    : filteredVouchers;

  // Filter chips data
  const filters = [
    { key: 'all', label: 'All' },
    { key: 'expiring_soon', label: `Expiring soon`, count: expiringSoon.length },
    { key: 'active', label: 'Active' },
    { key: 'used', label: 'Used', count: usedVouchers.length },
  ];

  const handleVoucherClick = (voucher) => {
    navigate(`/vouchers/${voucher.id}`);
  };

  const handleAddVoucher = () => {
    setShowScanModal(true);
  };

  const handleScanModalClose = () => {
    setShowScanModal(false);
  };

  const handleVoucherSaved = () => {
    fetchVouchers();
  };

  // User info
  const userName = authUser?.first_name || authUser?.name?.split(' ')[0] || 'User';
  const lastName = authUser?.last_name || '';
  const userEmail = authUser?.email || '';
  const avatarUrl = authUser?.avatar_url || authUser?.picture || '';

  // ── Loading skeleton ───────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F3F4F6' }}>
        <div className="animate-pulse flex flex-col items-center gap-4">
          <Ticket className="w-10 h-10" style={{ color: '#D1D5DB' }} />
          <div className="h-4 w-32 rounded-full" style={{ backgroundColor: '#E5E7EB' }} />
        </div>
      </div>
    );
  }

  // ── Shared components ──────────────────────────────

  const FilterChips = () => (
    <div className="flex items-center gap-2 overflow-x-auto" style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', msOverflowStyle: 'none' }}>
      {filters.map((f) => {
        const isActive = activeFilter === f.key;
        return (
          <button
            key={f.key}
            onClick={() => setActiveFilter(f.key)}
            className="flex-shrink-0 rounded-full transition-colors"
            style={{
              backgroundColor: isActive ? PRIMARY : '#FFFFFF',
              color: isActive ? '#FFFFFF' : '#374151',
              border: isActive ? 'none' : '1px solid #E5E7EB',
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'DM Sans, sans-serif',
              whiteSpace: 'nowrap',
            }}
            data-testid={`filter-${f.key}`}
          >
            {f.label}{f.count != null && f.count > 0 ? ` · ${f.count}` : ''}
          </button>
        );
      })}
    </div>
  );

  const ExpiringBanner = () => {
    if (expiringSoon.length === 0) return null;
    return (
      <div
        className="flex items-start gap-3"
        style={{
          backgroundColor: '#FFFBEB',
          border: '1px solid #FDE68A',
          borderRadius: 14,
          padding: '14px 16px',
        }}
        data-testid="expiring-banner"
      >
        <Bell className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#D97706' }} />
        <p style={{ color: '#92400E', fontSize: 13, fontFamily: 'DM Sans, sans-serif', lineHeight: 1.5 }}>
          <strong>{expiringSoon.length} voucher{expiringSoon.length > 1 ? 's' : ''}</strong> expiring within 7 days. We'll remind you the day before.
        </p>
      </div>
    );
  };

  const EmptyState = () => (
    <div className="flex flex-col items-center text-center" style={{ padding: '48px 24px' }}>
      <BuddyMascot size={100} />
      <h2
        className="mt-6"
        style={{ color: '#111827', fontSize: 22, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}
      >
        No vouchers yet
      </h2>
      <p
        className="mt-2"
        style={{ color: '#6B7280', fontSize: 15, fontFamily: 'DM Sans, sans-serif', maxWidth: 300 }}
      >
        Scan your vouchers and coupons to track them all in one place
      </p>
      <button
        onClick={handleAddVoucher}
        className="mt-6 flex items-center gap-2 rounded-full"
        style={{
          backgroundColor: PRIMARY,
          color: '#FFFFFF',
          padding: '14px 28px',
          fontSize: 15,
          fontWeight: 700,
          fontFamily: 'DM Sans, sans-serif',
          boxShadow: '0 4px 14px rgba(45,91,255,0.3)',
        }}
        data-testid="add-first-voucher"
      >
        <Ticket className="w-5 h-5" />
        Add first voucher
      </button>
    </div>
  );

  const VoucherList = () => (
    <div className="space-y-3">
      {displayVouchers.map((v) => (
        <VoucherCard key={v.id} voucher={v} onClick={handleVoucherClick} />
      ))}
      {displayVouchers.length === 0 && (
        <p className="text-center py-10" style={{ color: '#9CA3AF', fontSize: 14, fontFamily: 'DM Sans, sans-serif' }}>
          {searchQuery.trim() ? `No vouchers matching "${searchQuery}"` : 'No vouchers in this category'}
        </p>
      )}
    </div>
  );

  // ════════════════════════════════════════════════════
  //  MOBILE LAYOUT
  // ════════════════════════════════════════════════════

  if (isMobile) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F3F4F6' }} data-testid="vouchers-page">
        {/* Gradient header */}
        <header
          style={{
            background: GRADIENT_HEADER,
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 24px)',
            paddingBottom: 24,
            borderBottomLeftRadius: 28,
            borderBottomRightRadius: 28,
          }}
        >
          {/* Top row: back + title + scan */}
          <div className="flex items-center justify-between px-5 mb-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center"
              style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: 'rgba(255,255,255,0.15)' }}
              data-testid="back-btn"
            >
              <ChevronLeft className="w-5 h-5" style={{ color: '#FFFFFF' }} />
            </button>
            <div className="flex-1 px-4">
              <h1 style={{ color: '#FFFFFF', fontSize: 24, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
                Vouchers
              </h1>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>
                Coupons &amp; rewards, ready to use
              </p>
            </div>
            <button
              onClick={handleAddVoucher}
              className="flex items-center gap-1.5"
              style={{
                backgroundColor: '#FFFFFF',
                color: '#111827',
                borderRadius: 12,
                padding: '10px 16px',
                fontSize: 14,
                fontWeight: 700,
                fontFamily: 'DM Sans, sans-serif',
              }}
              data-testid="scan-btn"
            >
              <ScanLine className="w-4 h-4" />
              Scan
            </button>
          </div>

          {/* Search bar */}
          {vouchers.length > 0 && (
            <div style={{ padding: '0 20px' }}>
              <div
                className="flex items-center gap-2"
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: 12,
                  padding: '12px 14px',
                }}
              >
                <Search className="w-4 h-4 flex-shrink-0" style={{ color: '#9CA3AF' }} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search by merchant..."
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: '#111827',
                    fontSize: 16,
                    fontFamily: 'DM Sans, sans-serif',
                  }}
                  data-testid="voucher-search-input"
                />
                {searchQuery.length > 0 && (
                  <button
                    onClick={() => setSearchQuery('')}
                    style={{ color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          )}
        </header>

        {/* Body */}
        <div style={{ padding: '16px 20px', paddingBottom: 'calc(100px + env(safe-area-inset-bottom, 0px))' }}>
          {vouchers.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="space-y-4">
              <FilterChips />
              <ExpiringBanner />
              <VoucherList />
            </div>
          )}
        </div>

        {/* Bottom Navigation */}
        <nav
          className="fixed bottom-0 left-0 right-0 flex justify-around items-center"
          style={{
            backgroundColor: '#FFFFFF',
            borderTop: '1px solid #F3F4F6',
            paddingTop: 10,
            paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
            zIndex: 50,
          }}
          data-testid="bottom-nav"
        >
          {NAV_ITEMS.map((item) => {
            const active = activeNav === item.key;
            return (
              <button
                key={item.key}
                onClick={() => onNavClick(item.key)}
                className="flex flex-col items-center gap-1"
                data-testid={`bottom-nav-${item.key}`}
              >
                <item.icon className="w-5 h-5" style={{ color: active ? PRIMARY : '#9CA3AF' }} fill={active ? PRIMARY : 'none'} />
                <span style={{ color: active ? PRIMARY : '#9CA3AF', fontSize: 11, fontFamily: 'DM Sans, sans-serif', fontWeight: active ? 600 : 500 }}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>

        <ScanVoucherModal
          isOpen={showScanModal}
          onClose={handleScanModalClose}
          isMobile={true}
          onSaved={handleVoucherSaved}
        />
      </div>
    );
  }

  // ════════════════════════════════════════════════════
  //  DESKTOP LAYOUT
  // ════════════════════════════════════════════════════

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#F3F4F6' }} data-testid="vouchers-page">
      {/* Sidebar */}
      <aside className="flex flex-col" style={{ width: 240, backgroundColor: '#F9FAFB', borderRight: '1px solid #F3F4F6' }}>
        <div className="flex items-center gap-2 px-5 pt-6 pb-8">
          <Ticket className="w-7 h-7" style={{ color: PRIMARY }} />
          <span style={{ color: '#111827', fontSize: 18, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>
            My Buddy
          </span>
        </div>
        <nav className="flex-1 px-3">
          {NAV_ITEMS.map((item) => {
            const active = activeNav === item.key;
            return (
              <button
                key={item.key}
                onClick={() => onNavClick(item.key)}
                className="w-full flex items-center gap-3 px-4 py-3 mb-1 transition-all"
                style={{
                  backgroundColor: active ? '#E0E7FF' : 'transparent',
                  color: active ? PRIMARY : '#6B7280',
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: active ? 600 : 500,
                  fontFamily: 'DM Sans, sans-serif',
                }}
                data-testid={`sidebar-nav-${item.key}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t" style={{ borderColor: '#F3F4F6' }}>
          <div className="flex items-center gap-3">
            <UserAvatar name={userName} avatarUrl={avatarUrl} size={40} />
            <div className="min-w-0">
              <p className="truncate" style={{ color: '#111827', fontSize: 14, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>
                {userName} {lastName}
              </p>
              {userEmail && (
                <p className="truncate" style={{ color: '#6B7280', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>
                  {userEmail}
                </p>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        {/* Top bar */}
        <div
          className="flex items-center justify-between"
          style={{
            padding: '20px 32px',
            borderBottom: '1px solid #E5E7EB',
            backgroundColor: '#FFFFFF',
          }}
        >
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center hover:bg-gray-100 transition-colors"
              style={{ width: 36, height: 36, borderRadius: 10 }}
              data-testid="back-btn"
            >
              <ChevronLeft className="w-5 h-5" style={{ color: '#6B7280' }} />
            </button>
            <h1 style={{ color: '#111827', fontSize: 22, fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
              Vouchers
            </h1>
          </div>
          <button
            onClick={handleAddVoucher}
            className="flex items-center gap-2"
            style={{
              backgroundColor: PRIMARY,
              color: '#FFFFFF',
              borderRadius: 12,
              padding: '10px 20px',
              fontSize: 14,
              fontWeight: 700,
              fontFamily: 'DM Sans, sans-serif',
            }}
            data-testid="add-voucher-btn"
          >
            <Plus className="w-4 h-4" />
            Add voucher
          </button>
        </div>

        {/* Content area */}
        <div style={{ padding: '24px 32px', maxWidth: 900 }}>
          {vouchers.length === 0 ? (
            <div
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: 18,
                padding: '48px 40px',
                boxShadow: '0px 4px 16px rgba(0,0,0,0.05)',
              }}
            >
              <EmptyState />
            </div>
          ) : (
            <div className="space-y-5">
              {/* Search bar */}
              <div
                className="flex items-center gap-2"
                style={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  borderRadius: 12,
                  padding: '10px 16px',
                }}
              >
                <Search className="w-4 h-4 flex-shrink-0" style={{ color: '#9CA3AF' }} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search by merchant..."
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: '#111827',
                    fontSize: 16,
                    fontFamily: 'DM Sans, sans-serif',
                  }}
                  data-testid="voucher-search-input"
                />
                {searchQuery.length > 0 && (
                  <button
                    onClick={() => setSearchQuery('')}
                    style={{ color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Filters */}
              <FilterChips />

              {/* Expiring banner */}
              <ExpiringBanner />

              {/* Voucher list */}
              <VoucherList />
            </div>
          )}
        </div>
      </main>

      <ScanVoucherModal
        isOpen={showScanModal}
        onClose={handleScanModalClose}
        isMobile={false}
        onSaved={handleVoucherSaved}
      />
    </div>
  );
}